/**
 * Export utilities for converting data to different formats and downloading
 */

/**
 * Convert array of objects to CSV string
 * @param {Array} data - Array of objects to convert
 * @param {Array} headers - Array of header objects with key and label
 * @returns {string} - CSV string
 */
export const convertToCSV = (data, headers) => {
    if (!data || data.length === 0) {
        return "";
    }

    // Create header row
    const headerRow = headers.map((h) => h.label).join(",");

    // Create data rows
    const dataRows = data.map((row) => {
        return headers
            .map((header) => {
                let value = row[header.key];

                // Handle nested objects (e.g., customer.name)
                if (header.key.includes(".")) {
                    const keys = header.key.split(".");
                    value = keys.reduce((obj, key) => obj?.[key], row);
                }

                // Handle null/undefined
                if (value === null || value === undefined) {
                    return "";
                }

                // Handle dates
                if (value instanceof Date) {
                    value = value.toLocaleDateString();
                }

                // Handle objects
                if (typeof value === "object") {
                    value = JSON.stringify(value);
                }

                // Escape quotes and wrap in quotes if contains comma or newline
                value = String(value).replace(/"/g, '""');
                if (value.includes(",") || value.includes("\n") || value.includes('"')) {
                    value = `"${value}"`;
                }

                return value;
            })
            .join(",");
    });

    return [headerRow, ...dataRows].join("\n");
};

/**
 * Download data as CSV file
 * @param {string} csvContent - CSV string content
 * @param {string} filename - Filename for download
 */
export const downloadCSV = (csvContent, filename = "export.csv") => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * Download data as Excel file (using HTML table method)
 * @param {string} htmlTable - HTML table string
 * @param {string} filename - Filename for download
 */
export const downloadExcel = (htmlTable, filename = "export.xlsx") => {
    const blob = new Blob([htmlTable], {
        type: "application/vnd.ms-excel",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * Convert array of objects to HTML table for Excel export
 * @param {Array} data - Array of objects
 * @param {Array} headers - Array of header objects
 * @returns {string} - HTML table string
 */
export const convertToHTMLTable = (data, headers) => {
    if (!data || data.length === 0) {
        return "<table></table>";
    }

    let html = '<table border="1">';

    // Header row
    html += "<thead><tr>";
    headers.forEach((header) => {
        html += `<th>${header.label}</th>`;
    });
    html += "</tr></thead>";

    // Data rows
    html += "<tbody>";
    data.forEach((row) => {
        html += "<tr>";
        headers.forEach((header) => {
            let value = row[header.key];

            // Handle nested objects
            if (header.key.includes(".")) {
                const keys = header.key.split(".");
                value = keys.reduce((obj, key) => obj?.[key], row);
            }

            // Handle null/undefined
            if (value === null || value === undefined) {
                value = "";
            }

            // Handle dates
            if (value instanceof Date) {
                value = value.toLocaleDateString();
            }

            // Handle objects
            if (typeof value === "object") {
                value = JSON.stringify(value);
            }

            html += `<td>${value}</td>`;
        });
        html += "</tr>";
    });
    html += "</tbody>";
    html += "</table>";

    return html;
};

/**
 * Export orders data to CSV or Excel
 * @param {Object} reportData - Report data from API
 * @param {string} format - Export format ('csv' or 'excel')
 * @param {string} reportType - Type of report (for filename)
 */
export const exportOrdersReport = (reportData, format = "csv", reportType = "orders") => {
    if (!reportData || !reportData.orders || reportData.orders.length === 0) {
        alert("No data to export");
        return;
    }

    const headers = [
        { key: "order_number", label: "Order Number" },
        { key: "customer.name", label: "Customer Name" },
        { key: "customer.mobile", label: "Mobile" },
        { key: "product.name", label: "Product" },
        { key: "total_amount", label: "Amount" },
        { key: "payment_status", label: "Payment Status" },
        { key: "payment_type", label: "Payment Type" },
        { key: "created_at", label: "Created Date" },
    ];

    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `${reportType}_report_${timestamp}`;

    if (format === "csv") {
        const csvContent = convertToCSV(reportData.orders, headers);
        downloadCSV(csvContent, `${filename}.csv`);
    } else if (format === "excel") {
        const htmlTable = convertToHTMLTable(reportData.orders, headers);
        downloadExcel(htmlTable, `${filename}.xls`);
    }
};

/**
 * Export warranty report
 */
export const exportWarrantyReport = (reportData, format = "csv") => {
    if (!reportData || !reportData.warranty_registrations || reportData.warranty_registrations.length === 0) {
        alert("No data to export");
        return;
    }

    const headers = [
        { key: "registration_number", label: "Registration ID" },
        { key: "customer_name", label: "Customer Name" },
        { key: "mobile", label: "Mobile" },
        { key: "product.name", label: "Product" },
        { key: "dealer_name", label: "Dealer" },
        { key: "dealer_state", label: "Dealer State" },
        { key: "purchase_date", label: "Purchase Date" },
        { key: "created_at", label: "Created Date" },
    ];

    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `warranty_report_${timestamp}`;

    if (format === "csv") {
        const csvContent = convertToCSV(reportData.warranty_registrations, headers);
        downloadCSV(csvContent, `${filename}.csv`);
    } else {
        const htmlTable = convertToHTMLTable(reportData.warranty_registrations, headers);
        downloadExcel(htmlTable, `${filename}.xls`);
    }
};

/**
 * Export insurance report
 */
export const exportInsuranceReport = (reportData, format = "csv") => {
    if (!reportData || !reportData.insurance_renewals || reportData.insurance_renewals.length === 0) {
        alert("No data to export");
        return;
    }

    const headers = [
        { key: "insurance_number", label: "Insurance Number" },
        { key: "customer_name", label: "Customer Name" },
        { key: "mobile", label: "Mobile" },
        { key: "product.name", label: "Product" },
        { key: "premium_amount", label: "Premium Amount" },
        { key: "status", label: "Status" },
        { key: "validity_from", label: "Validity From" },
        { key: "validity_to", label: "Validity To" },
        { key: "created_at", label: "Created Date" },
    ];

    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `insurance_report_${timestamp}`;

    if (format === "csv") {
        const csvContent = convertToCSV(reportData.insurance_renewals, headers);
        downloadCSV(csvContent, `${filename}.csv`);
    } else {
        const htmlTable = convertToHTMLTable(reportData.insurance_renewals, headers);
        downloadExcel(htmlTable, `${filename}.xls`);
    }
};

/**
 * Export customers report
 */
export const exportCustomersReport = (reportData, format = "csv") => {
    if (!reportData || !reportData.customers || reportData.customers.length === 0) {
        alert("No data to export");
        return;
    }

    const headers = [
        { key: "name", label: "Customer Name" },
        { key: "mobile", label: "Mobile" },
        { key: "email", label: "Email" },
        { key: "region.name", label: "Region" },
        { key: "staff.name", label: "Created By" },
        { key: "created_at", label: "Created Date" },
    ];

    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `customers_report_${timestamp}`;

    if (format === "csv") {
        const csvContent = convertToCSV(reportData.customers, headers);
        downloadCSV(csvContent, `${filename}.csv`);
    } else {
        const htmlTable = convertToHTMLTable(reportData.customers, headers);
        downloadExcel(htmlTable, `${filename}.xls`);
    }
};

/**
 * Export users report
 */
export const exportUsersReport = (reportData, format = "csv") => {
    if (!reportData || !reportData.users || reportData.users.length === 0) {
        alert("No data to export");
        return;
    }

    const headers = [
        { key: "name", label: "Name" },
        { key: "mobile", label: "Mobile" },
        { key: "email", label: "Email" },
        { key: "role", label: "Role" },
        { key: "region.name", label: "Region" },
        { key: "is_active", label: "Active" },
        { key: "is_b2b", label: "B2B" },
        { key: "created_at", label: "Created Date" },
    ];

    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `users_report_${timestamp}`;

    if (format === "csv") {
        const csvContent = convertToCSV(reportData.users, headers);
        downloadCSV(csvContent, `${filename}.csv`);
    } else {
        const htmlTable = convertToHTMLTable(reportData.users, headers);
        downloadExcel(htmlTable, `${filename}.xls`);
    }
};
